import { ICardElementWithChildren } from "backend/model/model";
import React from "react";
import InputContainer from "./InputContainer";
import TextInput from "./TextInput";
import RichTextInput from "@qpr/client-api-base/ui/components/RichTextInput";
import { makeThemedStyles } from "@qpr/client-api-base/viewcomponents/utils";
import FormButton from "../buttons/FormButton";
import FileUploadInput from "@qpr/client-api-base/ui/components/FileUploadInput";

import { InputProp, FieldValue } from "backend/datainput";
import InputMeta from "./InputMeta";
import { If } from "react-if";
import Todo from "../misc/Todo";
import { TextField } from "@material-ui/core";
import UploadedFilesList from "@qpr/client-api-base/ui/components/UploadedFilesList";
const useStyles = makeThemedStyles("ValueInputForm", (theme) => ({
  root: {
    background: "rgb(255,255,255)",
    padding: "0px",
    paddingTop: "20px",
    marginTop: "20px",
    borderRadius: "20px",
    boxShadow: "0px 0px 2px rgba(215,235,222, 1)",
    width: "100%",
    display: "flex",
    flexWrap: "wrap",
    flexDirection: "row",
  },
  unitSymbol: {
    fontSize: "14px",
  },
  datetime: {
    "& .MuiInput-input": {
      fontSize: "16px",
    },
  },
}));

export type ButtonProp = {
  isDisabled?: boolean;
  tag: string;
  style: "submit" | "save" | "approve" | "reject" | "decline";
  text: string | JSX.Element;
  onClick?: (values: FieldValue[]) => void;
};

type Props = {
  element: ICardElementWithChildren;
  fields: InputProp[];
  buttons: ButtonProp[];
  onChange?: (values: FieldValue[]) => void;
  onIntermediateChange?: (values: FieldValue[]) => void;
};

const ValueInputForm = (props: Props) => {
  const classes = useStyles();
  const [formState, setFormState] = React.useState<FieldValue[]>(() => {
    let fieldId = 1;
    return props.fields.map((prop) => {
      fieldId += 1;
      return {
        id: String(fieldId),
        series: prop.series,
        value:
          typeof prop.initialValue !== "undefined"
            ? prop.initialValue
            : prop.series
            ? String(prop.element?.[prop.series] || "")
            : "",
        field: prop,
      };
    });
  });

  const [reRender, setReRender] = React.useState(1);
  const fieldErrors = React.useRef<Map<FieldValue, string[]>>(
    new Map<FieldValue, string[]>()
  );

  /*
  const [fieldErrors, setFieldErrors] = React.useState<
    Map<FieldValue, string[]>
  >(new Map<FieldValue, string[]>());
*/
  React.useEffect(() => {
    if (props.onIntermediateChange) {
      props.onIntermediateChange(formState);
    }
  }, [formState]);

  return (
    <div className={classes.root}>
      {formState.map((prop) => {
        if (prop.field.isHidden) return null;

        const key = JSON.stringify([
          prop.series,
          prop.id,
          prop.field.element?.id,
        ]);

        const errorMessages = fieldErrors.current.get(prop);
        if (prop.field.inputType === "info") {
          return (
            <InputContainer
              errorMessages={errorMessages}
              title={prop.field.title || ""}
              key={key}
            />
          );
        }
        if (prop.field.inputType === "attachments") {
          if (prop.field.isDisabled) {
            return (
              <InputContainer key={key} title={prop.field.title} width="100%">
                <UploadedFilesList values={prop.field.initialFiles} readOnly />
              </InputContainer>
            );
          }
          return (
            <InputContainer key={key} title={prop.field.title} width="100%">
              <FileUploadInput
                maxFiles={5}
                maxSize={10}
                accept={[
                  ".pdf",
                  ".doc",
                  ".docx",
                  ".ppt",
                  ".pptx",
                  ".xls",
                  ".xlsx",
                ]}
                disabled={prop.field.isDisabled}
                onFiles={(files: File[]) => {
                  setFormState((oldState) => {
                    return oldState.map((fieldValue) => {
                      if (fieldValue.id === prop.id) {
                        return {
                          ...fieldValue,
                          files: files,
                        };
                      }
                      return fieldValue;
                    });
                  });
                }}
                files={prop.field.initialFiles}
              />
            </InputContainer>
          );
        }
        if (prop.field.inputType === "comment") {
          if (prop.field.isDisabled) {
            return (
              <InputMeta key={key}>
                <div
                  dangerouslySetInnerHTML={{
                    __html: prop.field.initialValue || "",
                  }}
                />
              </InputMeta>
            );
          }

          return (
            <InputContainer
              title={prop.field.title || ""}
              key={key}
              errorMessages={errorMessages}
            >
              <RichTextInput
                value={prop.field.initialValue}
                onChange={(newValue) => {
                  setFormState((oldState) => {
                    return oldState.map((fieldValue) => {
                      if (fieldValue.id === prop.id) {
                        return {
                          ...fieldValue,
                          value: newValue,
                        };
                      }
                      return fieldValue;
                    });
                  });
                }}
              />
            </InputContainer>
          );
        }
        if (prop.field.inputType === "datetime") {
          return (
            <InputContainer
              title={prop.field.title || prop.field.series || ""}
              key={key}
              errorMessages={errorMessages}
            >
              <TextField
                fullWidth={prop.field.fullWidth}
                disabled={prop.field.isDisabled}
                className={classes.datetime}
                type="date"
                value={prop.value}
                onChange={(newValue) => {
                  setFormState((oldState) => {
                    return oldState.map((fieldValue) => {
                      if (fieldValue.id === prop.id) {
                        return {
                          ...fieldValue,
                          value: newValue.target.value,
                        };
                      }
                      return fieldValue;
                    });
                  });
                }}
              />
              <If condition={prop.field.unitSeries}>
                <div className={classes.unitSymbol}>
                  {prop.field.element?.[
                    prop.field.unitSeries || "ACT_unitname"
                  ] || ""}
                </div>
              </If>
            </InputContainer>
          );
        }
        if (prop.field.inputType === "string") {
          return (
            <InputContainer
              title={prop.field.title || prop.field.series || ""}
              key={key}
              errorMessages={errorMessages}
              width={prop.field.fullWidth ? "80%" : undefined}
            >
              <TextInput
                fullWidth={prop.field.fullWidth}
                disabled={prop.field.isDisabled}
                value={prop.value}
                onChange={(newValue) => {
                  setFormState((oldState) => {
                    return oldState.map((fieldValue) => {
                      if (fieldValue.id === prop.id) {
                        return {
                          ...fieldValue,
                          value: newValue,
                        };
                      }
                      return fieldValue;
                    });
                  });
                }}
              />
              <If condition={prop.field.unitSeries}>
                <div className={classes.unitSymbol}>
                  {prop.field.element?.[
                    prop.field.unitSeries || "ACT_unitname"
                  ] || ""}
                </div>
              </If>
            </InputContainer>
          );
        }
      })}
      <InputContainer width="100%">
        {props.buttons
          .filter((btn) => !btn.isDisabled)
          .map((btn) => {
            return (
              <FormButton
                style={btn.style}
                type="button"
                onClick={() => {
                  let failedFieldsCount = 0;
                  formState.forEach((state) => {
                    if (state.field.validate) {
                      const verifyResult = state.field.validate({
                        field: state,
                        allFields: formState,
                        actionTag: btn.tag,
                      });
                      if (verifyResult.success) {
                        fieldErrors.current.delete(state);
                      } else {
                        fieldErrors.current.set(
                          state,
                          verifyResult.errorMessages || []
                        );
                        failedFieldsCount += 1;
                      }
                    }
                  });

                  if (failedFieldsCount > 0) {
                    setReRender((value) => value + 1);
                  }

                  if (failedFieldsCount === 0) {
                    if (btn.onClick)
                      btn.onClick(formState.filter((i) => !i.field.isDisabled));
                  }
                }}
              >
                {btn.text}
              </FormButton>
            );
          })}
      </InputContainer>
    </div>
  );
};

export default ValueInputForm;
